import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Grid,
    Heading,
    HStack,
    Icon,
    Image,
    Progress,
    Stack,
} from '@chakra-ui/react';
import { useServicesDashboard } from 'context/ServicesDashboardContext';
import { useMemo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import IconPlatform from './IconPlatform';
import {
    ACS,
    APP_AUTH,
    APP_AUTH_GCCH,
    DIRECT_ROUTING,
    EIGHTBYEIGHT,
    MICROSOFT_GRAPH,
    OPERATOR_CONNECT,
    SBC,
    SRT,
    TPM,
    VENDOR_DEFINED,
    ZOOM,
    ZOOM_AUTH,
    statusPriorityOrder,
    GCC_HIGH,
    WEBEX,
    WEBEX_AUTH,
    OPERATOR_PLATFORM,
} from '../constants';
import { PlatformEnums } from 'utils/platforms';
import { getSimpleRedirectAuthServiceTypeByAppAuthTypeID } from 'services/platformRegistry';
import { accordionCategory, findServiceCategory } from './AccordionCategory';
import DashboardCard from './DashboardCard';
import SBCCardDetails from './SBCCardDetails';
import SRTCard from 'pages/services/new/ServiceDashboard/Services/SRT';
import TPMCard from './TPM/TPMCard';
import VendorCard from './VendorCard/Vendor/VendorCard';
import AppAuthCard from './AppAuthCard';
import { useDashboardMenu } from 'context/DashboardMenuContext';
import { GetCardStatus } from './helpers/cardStatusHelper';
import DashboardHydrationCardSkeleton from './DashboardHydrationCardSkeleton';
import { useServiceDashboardBulk } from 'hooks/useCustomQuery/useServiceDashboard';
import useGetOperatorPlatform from 'pages/telephony/numbers/queries/useGetOperatorPlatform';
import { Tooltip } from 'components/v4';
import { attentionPulse, sectionStatusTokens } from '../home/dashboardTokens';
import ServiceSectionGrid from './ServiceSectionGrid';
import { summariseServiceHealth } from './helpers/serviceHealth';

const OPERATOR_PLATFORM_CATEGORY_TITLE = 'Operator Platform';

// Matches accordionCategory's title for GCC High auth services.
const GCC_HIGH_CATEGORY_TITLE = 'Microsoft (GCC High)';

// (3134) Collapsed section header and platform logo, as in the TCAP Concept
// design.
const SECTION_HEADER_HEIGHT = '56px';
const SECTION_ICON_SIZE = '24px';

const DashboardAccordion = ({
    services = [],
    serviceWizard,
    companyData,
    companyAppAuth = [],
    msServiceAuth = {},
    AppAuthService,
    ZoomAppAuthService,
    EightByEightAppAuthService,
    RingCentralAppAuthService,
    DialPadAppAuthService,
    WebexAppAuthService,
    AppAuthServiceType,
    ZoomAppAuthServiceType,
    EightByEightAppAuthServiceType,
    RingCentralAppAuthServiceType,
    DialPadAppAuthServiceType,
    WebexAppAuthServiceType,
    GccHighAppAuthServiceType,
    GccHighAppAuthService,
    onServiceHealthChange,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const currentCompany = useSelector(
        (state) => state.navigationLists?.currentCompany,
    );
    const { operatorPlatform } = useGetOperatorPlatform(
        currentCompany,
        Boolean(currentCompany),
    );
    const serviceStatus = useServicesDashboard();
    const { isQueryDisabled, setIsQueryDisabled } = useDashboardMenu();

    // (3134) Every section starts collapsed and only the user opens one: no
    // section is kept open, auto-expanded, or remembered across reloads.
    const [expandedTitles, setExpandedTitles] = useState(() => new Set());

    useEffect(() => {
        setExpandedTitles(new Set());
    }, [currentCompany]);

    const toggleSection = useCallback((title) => {
        setExpandedTitles((prev) => {
            const next = new Set(prev);
            if (next.has(title)) {
                next.delete(title);
            } else {
                next.add(title);
            }
            return next;
        });
    }, []);

    // User would need to manually refresh if idle for more than 2 minutes
    useEffect(() => {
        if (!isQueryDisabled) {
            const timer = setTimeout(() => {
                setIsQueryDisabled(true);
            }, 120000);

            return () => {
                clearTimeout(timer);
            };
        }

        // reset refresh on navigation
        return () => {
            setIsQueryDisabled(false);
        };
    }, [isQueryDisabled, setIsQueryDisabled]);

    // The consolidated /Services list payload omits the voice policy / dial
    // plan / region detail in statusDisplay for DR and GCC High, so those
    // cards get stuck on the generic Provisioning label. Only GET /Service/{id}
    // returns that detail, so fetch it per-service for just these two types.
    const drGcchServiceItems = useMemo(
        () =>
            services
                .filter(
                    (service) =>
                        !service?.__isSkeleton &&
                        (service?.serviceType === DIRECT_ROUTING ||
                            service?.serviceType === GCC_HIGH),
                )
                .map((service) => ({
                    serviceType: service.serviceType,
                    serviceId:
                        service?.id ?? service?.serviceId ?? service?.serviceID,
                }))
                .filter((item) => item.serviceId),
        [services],
    );

    useServiceDashboardBulk(drGcchServiceItems, { enabled: !isQueryDisabled });

    const getAuthServiceCardStatus = useCallback(
        (authServiceType, platformID) => {
            let statusKey = authServiceType;
            if (authServiceType === MICROSOFT_GRAPH) statusKey = APP_AUTH;
            // The GCC High Microsoft auth shares appAuthTypeID 'Microsoft'; it
            // is disambiguated by platformID and stored under APP_AUTH_GCCH.
            if (
                authServiceType === MICROSOFT_GRAPH &&
                platformID === PlatformEnums.GCC
            )
                statusKey = APP_AUTH_GCCH;
            // Simple-redirect platforms (EightByEight, RingCentral, DialPad,
            // Zoom, Webex) receive the API's appAuthTypeID here; translate it
            // to the dashboard status-context key it's stored under.
            statusKey =
                getSimpleRedirectAuthServiceTypeByAppAuthTypeID(
                    authServiceType,
                ) ?? statusKey;
            return serviceStatus[statusKey] || {};
        },
        [serviceStatus],
    );

    const groupedServices = useMemo(() => {
        const allServices = [
            {
                serviceType: AppAuthServiceType,
                service: AppAuthService,
                type: 'auth',
            },
            {
                serviceType: ZoomAppAuthServiceType,
                service: ZoomAppAuthService,
                type: 'auth',
            },
            {
                serviceType: EightByEightAppAuthServiceType,
                service: EightByEightAppAuthService,
                type: 'auth',
            },
            {
                serviceType: RingCentralAppAuthServiceType,
                service: RingCentralAppAuthService,
                type: 'auth',
            },
            {
                serviceType: DialPadAppAuthServiceType,
                service: DialPadAppAuthService,
                type: 'auth',
            },
            {
                serviceType: WebexAppAuthServiceType,
                service: WebexAppAuthService,
                type: 'auth',
            },
            {
                serviceType: GccHighAppAuthServiceType,
                service: GccHighAppAuthService,
                platformID: PlatformEnums.GCC,
                type: 'auth',
            },
            ...services.map((service) => ({ ...service, type: 'regular' })),
        ].filter((service) => service.serviceType);

        const grouped = allServices.reduce((acc, service) => {
            let category = 'Other';
            let categoryIcon = null;
            let categoryImage = null;

            // Special handling for GCC High auth services - route to GCC High category
            if (
                service.type === 'auth' &&
                service.platformID === PlatformEnums.GCC
            ) {
                category = GCC_HIGH_CATEGORY_TITLE;
                categoryIcon = accordionCategory.find(
                    (c) => c.title === category,
                )?.icon;
            } else {
                const categoryObj = findServiceCategory(service);
                if (categoryObj) {
                    category = categoryObj.title;
                    categoryIcon = categoryObj.icon;
                    categoryImage = categoryObj.image ?? null;
                }
            }

            if (!acc[category]) {
                acc[category] = {
                    services: [],
                    authServices: [],
                    icon: categoryIcon,
                    image: categoryImage,
                };
            }

            if (
                service.type === 'auth' ||
                [ZOOM_AUTH, MICROSOFT_GRAPH, WEBEX_AUTH].includes(
                    service.serviceType,
                )
            ) {
                acc[category].authServices.push(service);
            } else {
                acc[category].services.push(service);
            }

            return acc;
        }, {});

        for (const category in grouped) {
            grouped[category].services.sort((a, b) => {
                const priorityServices = [
                    TPM,
                    DIRECT_ROUTING,
                    OPERATOR_CONNECT,
                    MICROSOFT_GRAPH,
                    EIGHTBYEIGHT,
                    ZOOM,
                    WEBEX,
                ];

                return (
                    priorityServices.indexOf(a.serviceType) -
                    priorityServices.indexOf(b.serviceType)
                );
            });
        }

        const categoryOrder = accordionCategory.map((cat) => cat.title);
        const sortedGroupedEntries = categoryOrder
            .filter(
                (categoryTitle) =>
                    grouped[categoryTitle] &&
                    (grouped[categoryTitle].services.length > 0 ||
                        grouped[categoryTitle].authServices.length > 0),
            )
            .map((categoryTitle) => [categoryTitle, grouped[categoryTitle]]);

        if (
            grouped['Other'] &&
            (grouped['Other'].services.length > 0 ||
                grouped['Other'].authServices.length > 0)
        ) {
            sortedGroupedEntries.push(['Other', grouped['Other']]);
        }
        const sortedGrouped = Object.fromEntries(sortedGroupedEntries);

        const finalGrouped = Object.entries(sortedGrouped).map(
            ([categoryTitle, accordionGroup]) => {
                const authCandidates = (accordionGroup.authServices || []).map(
                    (svc) => {
                        const authCard = getAuthServiceCardStatus(
                            svc.serviceType,
                            svc.platformID,
                        );
                        return authCard?.statusDisplay
                            ? {
                                  ...svc,
                                  statusDisplay: authCard.statusDisplay,
                                  statusLabel: authCard.statusLabel,
                                  color: authCard.color,
                                  animation: authCard.animation,
                                  hasStripes: authCard.hasStripes,
                              }
                            : svc;
                    },
                );

                const enrichedServices = (accordionGroup.services || []).map(
                    (svc) => {
                        const serviceCard = serviceStatus[svc.serviceType];

                        // Reducer status from a previously-viewed company is
                        // stale; keep the fresh list statusDisplay instead.
                        const isStale =
                            serviceCard?.serviceId !== undefined &&
                            serviceCard?.serviceId !== svc.id;

                        return serviceCard?.statusDisplay && !isStale
                            ? {
                                  ...svc,
                                  statusDisplay: serviceCard.statusDisplay,
                                  statusLabel: serviceCard.statusLabel,
                                  color: serviceCard.color,
                                  animation: serviceCard.animation,
                                  hasStripes: serviceCard.hasStripes,
                              }
                            : svc;
                    },
                );

                const candidates = [...enrichedServices, ...authCandidates];

                const { bestService } = candidates.reduce(
                    (acc, service) => {
                        const level =
                            service?.statusDisplay?.statusDisplayLevel;
                        if (level === undefined) return acc;

                        const idx = statusPriorityOrder.indexOf(level);
                        if (idx === -1) return acc;

                        if (idx < acc.bestIndex) {
                            return { bestIndex: idx, bestService: service };
                        }
                        return acc;
                    },
                    { bestIndex: Number.POSITIVE_INFINITY, bestService: null },
                );

                return [
                    categoryTitle,
                    {
                        ...accordionGroup,
                        // Fall back to the first card when no candidate has a
                        // statusDisplayLevel in statusPriorityOrder, so the
                        // category progress still reflects its service card
                        // (e.g. 8x8) instead of being undefined.
                        statusPriority: bestService ?? candidates[0] ?? null,
                    },
                ];
            },
        );
        return Object.fromEntries(finalGrouped);
    }, [
        AppAuthServiceType,
        AppAuthService,
        ZoomAppAuthServiceType,
        ZoomAppAuthService,
        EightByEightAppAuthServiceType,
        EightByEightAppAuthService,
        WebexAppAuthServiceType,
        WebexAppAuthService,
        GccHighAppAuthServiceType,
        GccHighAppAuthService,
        services,
        getAuthServiceCardStatus,
        RingCentralAppAuthService,
        RingCentralAppAuthServiceType,
        DialPadAppAuthService,
        DialPadAppAuthServiceType,
        serviceStatus,
    ]);

    const getServiceKey = (service, idx) => {
        const id = service?.serviceId ?? service?.serviceID ?? service?.id;
        if (id) return `${service?.serviceType}-${id}`;
        return `${service?.serviceType}-${idx}`;
    };

    // Resolve the app auth that belongs to a service. Commercial (OC/DR) and
    // GCC High services both use a Microsoft app auth (same appAuthTypeID
    // 'Microsoft'), so disambiguate by platformID — otherwise the first
    // Microsoft match wins and a commercial service can pair with the GCCH auth.
    const resolveServiceAppAuth = (service) => {
        if (!service?.appAuthType) return undefined;
        const expectedMsPlatform =
            service.serviceType === GCC_HIGH
                ? PlatformEnums.GCC
                : PlatformEnums.TEAMS;
        return companyAppAuth?.find((appAuth) => {
            if (!service.appAuthType.includes(appAuth.appAuthTypeID)) {
                return false;
            }
            if (appAuth.appAuthTypeID === MICROSOFT_GRAPH) {
                return appAuth.platformID === expectedMsPlatform;
            }
            return true;
        });
    };

    const renderServiceCard = (service, uniqueKey, animationDelayMs = 0) => {
        if (service?.__isSkeleton) {
            return (
                <DashboardHydrationCardSkeleton
                    key={uniqueKey}
                    service={service}
                    animationDelayMs={animationDelayMs}
                />
            );
        }

        switch (service.serviceType) {
            case TPM:
                return (
                    <TPMCard
                        key={uniqueKey}
                        service={service}
                        companyData={companyData}
                        serviceWizard={serviceWizard}
                    />
                );
            case ACS:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        companyData={companyData}
                    />
                );
            case OPERATOR_CONNECT:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            case DIRECT_ROUTING:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            case SBC:
                return <SBCCardDetails key={uniqueKey} service={service} />;
            case SRT:
                return <SRTCard key={uniqueKey} service={service} />;
            case VENDOR_DEFINED:
            case OPERATOR_PLATFORM:
                return (
                    <VendorCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                    />
                );
            case GCC_HIGH:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                        companyAppAuth={resolveServiceAppAuth(service)}
                    />
                );
            default:
                return (
                    <DashboardCard
                        key={uniqueKey}
                        service={service}
                        serviceWizard={serviceWizard}
                        msServiceAuth={msServiceAuth}
                        hasZoomAppAuth={Boolean(ZoomAppAuthService)}
                        hasEightByEightAppAuth={Boolean(
                            EightByEightAppAuthService,
                        )}
                        hasWebexAppAuth={Boolean(WebexAppAuthService)}
                        companyData={companyData}
                    />
                );
        }
    };

    const cardStatus = useCallback(
        (service) => {
            return GetCardStatus(service, serviceStatus);
        },
        [serviceStatus],
    );

    const serviceHealth = useMemo(
        () =>
            summariseServiceHealth(
                groupedServices,
                (svc) => cardStatus(svc)?.color,
                (svc) =>
                    getAuthServiceCardStatus(svc.serviceType, svc.platformID)
                        ?.color,
            ),
        [cardStatus, getAuthServiceCardStatus, groupedServices],
    );

    // (3133/3153) The account summary's attention flag and the notifications
    // read the same health the sections pulse on.
    useEffect(() => {
        onServiceHealthChange?.(serviceHealth);
    }, [onServiceHealthChange, serviceHealth]);

    const sections = Object.entries(groupedServices).map(
        ([categoryTitle, categoryData], categoryIdx) => {
            const isExpanded = expandedTitles.has(categoryTitle);
            // (3157) A section needing attention pulses until it is opened.
            const isPulsing =
                !isExpanded &&
                serviceHealth.attentionTitles.includes(categoryTitle);
            const displayTitle =
                categoryTitle === 'Other'
                    ? 'Services without Authentication'
                    : categoryTitle === OPERATOR_PLATFORM_CATEGORY_TITLE &&
                        operatorPlatform?.name
                      ? operatorPlatform.name
                      : categoryTitle;
            const currentCardStatus = cardStatus(categoryData?.statusPriority);
            // (3134) The header is tinted by the section's status, as in the
            // TCAP Concept design.
            const sectionStatus = sectionStatusTokens(
                currentCardStatus?.color,
                darkMode,
            );
            const authService = categoryData?.authServices[0];

            return (
                <Accordion
                    key={categoryTitle}
                    index={isExpanded ? 0 : -1}
                    onChange={() => toggleSection(categoryTitle)}
                    allowToggle
                    reduceMotion>
                    <AccordionItem
                        sx={{
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: sectionStatus.accent,
                            boxShadow: darkMode ? 'none' : 'sm',
                            ...(isPulsing ? attentionPulse : {}),
                        }}
                        borderRadius="8px"
                        bg="transparent">
                        <AccordionButton
                            borderRadius={isExpanded ? '7px 7px 0 0' : '7px'}
                            position="relative"
                            height={SECTION_HEADER_HEIGHT}
                            py={2}
                            pl={5}
                            bg={sectionStatus.bg}
                            _hover={{ bg: sectionStatus.bg }}>
                            {/* Progress Bar - Left Border */}
                            <Progress
                                value={100}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: '4px',
                                    borderRadius: isExpanded
                                        ? '7px 0 0 0'
                                        : '7px 0 0 7px',
                                }}
                                colorScheme={currentCardStatus?.color}
                                hasStripe={
                                    currentCardStatus?.animation ||
                                    currentCardStatus?.isAnimated
                                }
                                isAnimated={
                                    currentCardStatus?.animation ||
                                    currentCardStatus?.isAnimated
                                }
                            />
                            <HStack
                                spacing={3}
                                flex="1"
                                minW={0}
                                textAlign="left">
                                {categoryData.image ? (
                                    <Image
                                        src={categoryData.image}
                                        alt=""
                                        boxSize={SECTION_ICON_SIZE}
                                        objectFit="contain"
                                        flexShrink={0}
                                    />
                                ) : (
                                    categoryData.icon && (
                                        <Icon
                                            as={categoryData.icon}
                                            boxSize={SECTION_ICON_SIZE}
                                            flexShrink={0}
                                            color={darkMode ? 'white' : 'black'}
                                        />
                                    )
                                )}
                                <HStack spacing={2} flex="1" minW={0}>
                                    {/* Category Title */}
                                    <Tooltip
                                        label={displayTitle}
                                        hasArrow
                                        openDelay={300}>
                                        <Heading
                                            as="h2"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            noOfLines={1}
                                            minW={0}
                                            color={
                                                darkMode ? 'white' : 'gray.800'
                                            }>
                                            {displayTitle}
                                        </Heading>
                                    </Tooltip>
                                </HStack>
                            </HStack>
                            {/* Collapsed View - Service Icons */}
                            {!isExpanded && (
                                <HStack
                                    spacing={2}
                                    as="span"
                                    flexShrink={0}
                                    marginRight={3}
                                    marginLeft={2}>
                                    {categoryData.services.map(
                                        (service, idx) => (
                                            <IconPlatform
                                                key={`${categoryTitle}-icon-${getServiceKey(
                                                    service,
                                                    idx,
                                                )}`}
                                                service={service}
                                                companyData={companyData}
                                            />
                                        ),
                                    )}
                                </HStack>
                            )}
                            <Box as="span" flexShrink={0}>
                                <AccordionIcon />
                            </Box>
                        </AccordionButton>

                        {isExpanded && (
                            <AccordionPanel
                                p={3}
                                borderRadius="0 0 8px 8px"
                                backgroundColor={
                                    darkMode ? '#000000' : 'white'
                                }>
                                <Stack spacing={3}>
                                    {/* The app auth heads the section as a
                                        compact row, outside the card grid so
                                        it isn't stretched to card height. */}
                                    {authService?.serviceType &&
                                        (authService.__isSkeleton ? (
                                            <DashboardHydrationCardSkeleton
                                                service={authService}
                                                animationDelayMs={
                                                    categoryIdx * 140
                                                }
                                            />
                                        ) : (
                                            <AppAuthCard
                                                categoryData={categoryData}
                                                msServiceAuth={msServiceAuth}
                                                hasEightByEightAppAuth={Boolean(
                                                    EightByEightAppAuthService,
                                                )}
                                                hasZoomAppAuth={Boolean(
                                                    ZoomAppAuthService,
                                                )}
                                                hasWebexAppAuth={Boolean(
                                                    WebexAppAuthService,
                                                )}
                                                serviceWizard={serviceWizard}
                                            />
                                        ))}
                                    {/* (3158) One card per row, each row as
                                        tall as the tallest, so cards match. */}
                                    {categoryData.services.length > 0 && (
                                        <Grid
                                            templateColumns="minmax(0, 1fr)"
                                            autoRows="1fr"
                                            gap={3}>
                                            {categoryData.services.map(
                                                (service, idx) =>
                                                    renderServiceCard(
                                                        service,
                                                        `${categoryTitle}-card-${getServiceKey(service, idx)}`,
                                                        categoryIdx * 140 +
                                                            (idx + 1) * 90,
                                                    ),
                                            )}
                                        </Grid>
                                    )}
                                </Stack>
                            </AccordionPanel>
                        )}
                    </AccordionItem>
                </Accordion>
            );
        },
    );

    return (
        <Box mb={6}>
            <ServiceSectionGrid sections={sections} />
        </Box>
    );
};

export default DashboardAccordion;
